import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams, Outlet, useNavigate } from 'react-router-dom'
import { Spin } from 'antd'
import PropTypes from 'prop-types'
import { DO_LOAD, IS_NEW } from '../../config'
import { useStores } from '../../cls/stores/MobXProvider'
/**
 * PageLoader handles fetching and polling of a single item by ID from the URL.
 * Displays a spinner while loading, an error message if not found, and renders
 * nested routes via <Outlet/> when ready.
 *
 * @component
 * @param {Object} props
 * @param {boolean} [props.forceLoad=false]
 *   – If true, always re-fetch even if item is already loaded.
 * @param {function} props.requireItm
 *   – Function to synchronously retrieve the current item instance by ID.
 * @param {function} props.loadItm
 *   – Async function to load the item by ID, returns a Promise.
 * @param {string} props.targetLst
 *   – API list name used by `isModifiedItm` to check for server-side modifications.
 * @returns {JSX.Element|null}
 */
const PageLoader = ({ forceLoad, requireItm, loadItm, targetLst }) => {
	const navigate = useNavigate()
	const [loadingItm, setLoadingItm] = useState(true)
	const { itmId } = useParams()
	const { users } = useStores()

	useEffect(() => {
		// Redirect to /users/:loggedInUserId/edit if itmId is 'profile'
		if (itmId === 'profile') {
			const queryParams = new URLSearchParams(window.location.search)
			// Redirect /users/profile/edit to /users/:loggedInUserId/edit with query params
			navigate(
				`/users/${users.loggedInUserId}/edit?${queryParams.toString()}`,
				{
					replace: true
				}
			)
		}
	}, [itmId])

	const _itm = requireItm(itmId)
	const [errorKey, setErrorKey] = useState(null)
	/*
	* Loading modified item goes trough user now
	useEffect(() => {
		// Function to cancel interval
		const cancelCheckAndLoad = () => {
			if (intervalIdRef.current) {
				clearInterval(intervalIdRef.current)
			}
		}

		// Function to check if the item is modified and load it if necessary
		const checkAndLoadItem = () => {
			if (_itm?.modified && targetLst && errorKey !== 'not_found') {
				isModifiedItm(_itm.modified, targetLst, { id: itmId })
					.then((res) => {
						if (res) {
							console.log(
								dateTimeNow(),
								'item was modified, loading...'
							)
							loadItm(itmId)
								.then(() => {
									if (_itm._class === 'User') {
										if (itmId === users.loggedInUserId) {
											users.loggedInUser.setTrackModified(
												_itm.modifiedTs
											)
										}
										users.saveState()
									}
								})
								.catch((err) => {
									// This will catch errors thrown by wrapWithLoading
									console.error('Error loading item:', err)

									// Handle the specific error by setting the errorKey
									if (
										err &&
										err.status === 'error' &&
										err?.reason !== ''
									) {
										setErrorKey(err.reason)
									} else {
										setErrorKey(null)
									}
								})
						}
					})
					.catch(console.error)
			} else if (errorKey === 'not_found') {
				cancelCheckAndLoad()
			}
		}

		// Load item initially
		if (_itm.loaded === DO_LOAD || (_itm.loaded !== IS_NEW && forceLoad)) {
			// itmId is 'profile' when the user navigates to /users/profile/edit
			loadItm(itmId === 'profile' ? users.loggedInUserId : itmId)
				.catch((err) => {
					// This will catch errors thrown by wrapWithLoading
					console.error('Error loading item:', err)

					// Handle the specific error by setting the errorKey
					if (err && err.status === 'error' && err?.reason !== '') {
						setErrorKey(err.reason)
					} else {
						setErrorKey(null)
					}
				})
				.finally(() => {
					setLoadingItm(false)
				})
		} else {
			setLoadingItm(false)
		}

		// Polling interval to check for modifications every x seconds
		if (!!!_itm.to_delete) {
			intervalIdRef.current = setInterval(
				checkAndLoadItem,
				modifiedFrequency * 1000
			)
		} else {
			cancelCheckAndLoad()
		}

		// Clean up interval on component unmount
		return () => cancelCheckAndLoad()
	}, [itmId, _itm.modified, targetLst, errorKey]) // Ensure dependencies are correctly set
	*/

	// Load item initially
	useEffect(() => {
		if (_itm.loaded === DO_LOAD || (_itm.loaded !== IS_NEW && forceLoad)) {
			// itmId is 'profile' when the user navigates to /users/profile/edit
			loadItm(itmId === 'profile' ? users.loggedInUserId : itmId)
				.catch((err) => {
					// This will catch errors thrown by wrapWithLoading
					console.error('Error loading item:', err)
					// Handle the specific error by setting the errorKey
					if (err && err.status === 'error' && err?.reason !== '') {
						setErrorKey(err.reason)
					} else {
						setErrorKey(null)
					}
				})
				.finally(() => {
					setLoadingItm(false)
				})
		} else {
			setLoadingItm(false)
		}
	}, [itmId, _itm.modified, targetLst, errorKey])

	if (loadingItm) {
		return (
			<div style={{ textAlign: 'center' }}>
				<p>Loading...</p>
				<Spin />
			</div>
		)
	}

	if (errorKey === 'not_found') {
		return <div style={{ textAlign: 'center' }}>Not found</div>
	}

	return (
		<>
			<Outlet />
		</>
	)
}

PageLoader.propTypes = {
	forceLoad: PropTypes.bool,
	requireItm: PropTypes.func.isRequired,
	loadItm: PropTypes.func.isRequired,
	targetLst: PropTypes.string.isRequired
}

export default observer(PageLoader)
